import axios from "axios";
import { API_URL } from "../../constants";

export const CART_SIZE_LIMIT_BYTES = 50 * 1024 * 1024;
export const CART_SIZE_LIMIT_LABEL = "50 MB";

export interface DocumentCartItem {
  id: string;
  fileId: string;
  parentFile: string | null;
  sourceCategoryId: string | null;
  sourceCategoryPath?: string;
  addedAt: number | null;
  title: string | null;
  categoryId: string | null;
  categoryPath: string;
  available: boolean;
}

export interface ParentChildRow<T> {
  item: T;
  isChild: boolean;
  depth: number;
}

/**
 * Nest cart/history rows under their parents, including deeper descendants.
 * Items whose parent is also present are emitted under that parent (not dropped).
 */
export function arrangeParentAndChildRows<
  T extends { fileId: string; parentFile?: string | null }
>(items: T[]): ParentChildRow<T>[] {
  const itemIds = new Set(items.map((item) => item.fileId));
  const childrenByParent = new Map<string, T[]>();

  for (const item of items) {
    if (!item.parentFile || !itemIds.has(item.parentFile)) continue;
    const children = childrenByParent.get(item.parentFile) || [];
    children.push(item);
    childrenByParent.set(item.parentFile, children);
  }

  const arranged: ParentChildRow<T>[] = [];
  const visited = new Set<string>();

  const emitSubtree = (item: T, depth: number) => {
    if (visited.has(item.fileId)) return;
    visited.add(item.fileId);
    arranged.push({ item, isChild: depth > 0, depth });
    for (const child of childrenByParent.get(item.fileId) || []) {
      emitSubtree(child, depth + 1);
    }
  };

  for (const item of items) {
    if (item.parentFile && itemIds.has(item.parentFile)) continue;
    emitSubtree(item, 0);
  }

  // Orphans / cycles: still show any remaining rows.
  for (const item of items) {
    if (!visited.has(item.fileId)) {
      emitSubtree(item, item.parentFile && itemIds.has(item.parentFile) ? 1 : 0);
    }
  }

  return arranged;
}

export interface DocumentCartListResponse {
  items: DocumentCartItem[];
  count: number;
  availableCount: number;
  totalSizeBytes?: number | null;
  maxSizeBytes?: number;
}

export interface AddFileResponse {
  item: DocumentCartItem;
  count: number;
  alreadyInCart: boolean;
  truncated?: boolean;
  skippedChildren?: number;
}

export interface AddFolderResponse {
  added: number;
  skipped: number;
  truncated?: boolean;
  truncatedBySize?: boolean;
  count: number;
  alreadyInCart?: boolean;
  maxBytes?: number;
}

export interface FolderPreviewCategory {
  id: string;
  title: string;
  parentId: string | null;
  depth: number;
  fileCount: number;
  newFileCount: number;
  alreadyInCartCount: number;
  newSizeBytes?: number;
}

export interface FolderPreview {
  categoryId: string;
  categoryTitle: string;
  fileCount: number;
  newFileCount: number;
  alreadyInCartCount: number;
  wouldExceedCap: boolean;
  maxCartItems: number;
  currentCartCount: number;
  maxCartBytes?: number;
  currentCartBytes?: number | null;
  folderSizeBytes?: number;
  sampleTitles: string[];
  categories?: FolderPreviewCategory[];
}

export async function fetchDocumentCart(
  companyId: string
): Promise<DocumentCartListResponse> {
  const res = await axios.get(`${API_URL}/${companyId}/document-cart`);
  return res.data;
}

export async function fetchDocumentCartCount(
  companyId: string
): Promise<number> {
  const res = await axios.get(`${API_URL}/${companyId}/document-cart/count`);
  return res.data.count ?? 0;
}

export async function addFileToCart(
  companyId: string,
  fileId: string,
  source: "document" | "profile" = "document"
): Promise<AddFileResponse> {
  const res = await axios.post(`${API_URL}/${companyId}/document-cart/items`, {
    fileId,
    source,
  });
  return res.data;
}

export async function addFolderToCart(
  companyId: string,
  categoryId: string,
  recursive = true,
  categoryIds?: string[],
  source: "document" | "profile" = "document"
): Promise<AddFolderResponse> {
  const res = await axios.post(`${API_URL}/${companyId}/document-cart/items`, {
    categoryId,
    recursive,
    source,
    ...(categoryIds?.length ? { categoryIds } : {}),
  });
  return res.data;
}

export async function previewFolderForCart(
  companyId: string,
  categoryId: string,
  recursive = true,
  source: "document" | "profile" = "document"
): Promise<FolderPreview> {
  const res = await axios.post(
    `${API_URL}/${companyId}/document-cart/preview-folder`,
    { categoryId, recursive, source }
  );
  return res.data;
}

export async function removeCartItem(
  companyId: string,
  itemId: string
): Promise<{ removedId: string; count: number }> {
  const res = await axios.delete(
    `${API_URL}/${companyId}/document-cart/items/${itemId}`
  );
  return res.data;
}

export async function removeFolderFromCart(
  companyId: string,
  categoryId: string
): Promise<{ removed: number; categoryId: string; count: number }> {
  const res = await axios.delete(
    `${API_URL}/${companyId}/document-cart/folders/${categoryId}`
  );
  return res.data;
}

export async function clearDocumentCart(
  companyId: string
): Promise<{ count: number }> {
  const res = await axios.delete(`${API_URL}/${companyId}/document-cart`);
  return res.data;
}

export interface DownloadOrder {
  id: string;
  orderNumber: string;
  status: string;
  fileCount: number;
  totalSizeBytes: number;
  zipFileId?: string | null;
  expiresAt?: number | null;
  completedAt?: number | null;
  errorMessage?: string | null;
  createdAt?: number | null;
  items?: DownloadOrderItem[];
}

export interface DownloadOrderItem {
  id: string;
  fileId: string;
  parentFile?: string | null;
  fileTitle: string | null;
  categoryPath: string;
  fileSizeBytes: number;
  sourceCategoryId?: string | null;
}

export async function checkoutDocumentCart(companyId: string): Promise<{
  mode: "sync" | "async";
  order: DownloadOrder;
  skipped?: DocumentCartItem[];
  downloadUrl?: string;
}> {
  const res = await axios.post(
    `${API_URL}/${companyId}/document-cart/checkout`
  );
  return res.data;
}

export async function fetchDownloadOrders(
  companyId: string,
  params?: { page?: number; pageSize?: number; userId?: string }
): Promise<{
  orders: DownloadOrder[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}> {
  const res = await axios.get(`${API_URL}/${companyId}/download-orders`, {
    params,
  });
  return res.data;
}

export async function fetchDownloadOrder(
  companyId: string,
  orderId: string
): Promise<{ order: DownloadOrder }> {
  const res = await axios.get(
    `${API_URL}/${companyId}/download-orders/${orderId}`
  );
  return res.data;
}

export function getDownloadOrderZipUrl(
  companyId: string,
  orderId: string
): string {
  return `${API_URL}/${companyId}/download-orders/${orderId}/zip`;
}

function fileNameFromContentDisposition(
  header: string | undefined,
  fallback: string
): string {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain?.[1]) {
    try {
      return decodeURIComponent(plain[1].trim());
    } catch {
      return plain[1].trim();
    }
  }
  return fallback;
}

const inFlightZipDownloads = new Map<string, Promise<void>>();
const ZIP_DOWNLOAD_RETRY_DELAY_MS = 1_000;
const ZIP_DOWNLOAD_MAX_ATTEMPTS = 6;

/** Same-page ZIP save (no new tab). Auth via axios credentials. */
export async function downloadOrderZip(
  companyId: string,
  orderId: string,
  preferredFileName?: string
): Promise<void> {
  const dedupeKey = `${companyId}:${orderId}`;
  const existing = inFlightZipDownloads.get(dedupeKey);
  if (existing) return existing;

  const run = (async () => {
    try {
      for (let attempt = 1; attempt <= ZIP_DOWNLOAD_MAX_ATTEMPTS; attempt++) {
        const response = await axios.get(
          getDownloadOrderZipUrl(companyId, orderId),
          { responseType: "blob" }
        );

        if (response.status === 202) {
          if (attempt === ZIP_DOWNLOAD_MAX_ATTEMPTS) {
            throw new Error("ZIP_PROCESSING");
          }
          await new Promise((resolve) =>
            window.setTimeout(resolve, ZIP_DOWNLOAD_RETRY_DELAY_MS)
          );
          continue;
        }

        const contentType = String(response.headers["content-type"] || "");
        if (contentType.includes("application/json")) {
          const text = await (response.data as Blob).text();
          let message = "ZIP_DOWNLOAD_FAILED";
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || parsed.error || message;
          } catch {
            /* ignore */
          }
          throw new Error(message);
        }

        const fileName = fileNameFromContentDisposition(
          response.headers["content-disposition"],
          preferredFileName || `${orderId}.zip`
        );

        const blob = new Blob([response.data], { type: "application/zip" });
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.rel = "noopener";
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(objectUrl);
        return;
      }
    } finally {
      inFlightZipDownloads.delete(dedupeKey);
    }
  })();

  inFlightZipDownloads.set(dedupeKey, run);
  return run;
}
